"use client"

import { useState, useEffect, useCallback } from "react"
import { supabase } from "../../supabaseClient"
import { useAuth } from "../../context/AuthContext"
import { applyInstitutionFilter, ensureInstitutionOnPayload } from "../../lib/tenant"
import "../../styles/ProfessorForm.css"

const DIAS_SEMANA = [
  { key: "seg", label: "Segunda-feira" },
  { key: "ter", label: "Terça-feira" },
  { key: "qua", label: "Quarta-feira" },
  { key: "qui", label: "Quinta-feira" },
  { key: "sex", label: "Sexta-feira" },
  { key: "sab", label: "Sábado" },
]

export default function ProfessorForm({ onAdded, professorParaEditar, onCancelEdit }) {
  const { isAdmin, instituicaoId } = useAuth()
  const [nome, setNome] = useState("")
  const [departamento, setDepartamento] = useState("")
  const [curso, setCurso] = useState("")
  const [preferencia, setPreferencia] = useState([])
  const [disciplinasSelecionadas, setDisciplinasSelecionadas] = useState([])
  const [turmasSelecionadas, setTurmasSelecionadas] = useState([])
  const [loading, setLoading] = useState(false)
  const [isEditing, setIsEditing] = useState(false)

  const [disponibilidade, setDisponibilidade] = useState({
    seg: { entrada: "", saida: "" },
    ter: { entrada: "", saida: "" },
    qua: { entrada: "", saida: "" },
    qui: { entrada: "", saida: "" },
    sex: { entrada: "", saida: "" },
    sab: { entrada: "", saida: "" },
  })

  const [departamentosList, setDepartamentosList] = useState([])
  const [cursosList, setCursosList] = useState([])
  const [disciplinasList, setDisciplinasList] = useState([])
  const [turmasList, setTurmasList] = useState([])

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        let deptQuery = supabase.from("departamentos").select("*").order("nome")
        deptQuery = applyInstitutionFilter(deptQuery, isAdmin, instituicaoId)
        const { data: departamentos } = await deptQuery
        
        let cursosQuery = supabase.from("cursos").select("*").order("nome")
        cursosQuery = applyInstitutionFilter(cursosQuery, isAdmin, instituicaoId)
        const { data: cursos } = await cursosQuery
        
        let discQuery = supabase.from("disciplinas").select("*").order("nome")
        discQuery = applyInstitutionFilter(discQuery, isAdmin, instituicaoId)
        const { data: disciplinas } = await discQuery
        
        let turmasQuery = supabase.from("turmas").select("*").order("nome")
        turmasQuery = applyInstitutionFilter(turmasQuery, isAdmin, instituicaoId)
        const { data: turmas } = await turmasQuery

        setDepartamentosList(departamentos || [])
        setCursosList(cursos || [])
        setDisciplinasList(disciplinas || [])
        setTurmasList(turmas || [])
      } catch (err) {
        console.error("Erro ao carregar opções:", err.message)
      }
    }
    fetchOptions()
  }, [isAdmin, instituicaoId])

  const carregarAssociacoes = useCallback(async (professorId) => {
    try {
      let queryDisc = supabase
        .from("professor_disciplina")
        .select("disciplina_id")
        .eq("professor_id", professorId)
      if (!isAdmin && instituicaoId) {
        queryDisc = queryDisc.eq("instituicao_id", instituicaoId)
      }
      const { data: discData } = await queryDisc
      setDisciplinasSelecionadas((discData || []).map((d) => d.disciplina_id))

      let queryTurma = supabase
        .from("professor_turma")
        .select("turma_id")
        .eq("professor_id", professorId)
      if (!isAdmin && instituicaoId) {
        queryTurma = queryTurma.eq("instituicao_id", instituicaoId)
      }
      const { data: turmaData } = await queryTurma
      setTurmasSelecionadas((turmaData || []).map((t) => t.turma_id))
    } catch (err) {
      console.error("Erro ao carregar associações:", err.message)
    }
  }, [isAdmin, instituicaoId])

  useEffect(() => {
    if (professorParaEditar) {
      setIsEditing(true)
      setNome(professorParaEditar.nome || "")
      setDepartamento(professorParaEditar.departamento_id || "")
      setCurso(professorParaEditar.curso_id || "")
      setPreferencia(
        Array.isArray(professorParaEditar.preferencia_periodo)
          ? professorParaEditar.preferencia_periodo
          : professorParaEditar.preferencia_periodo
            ? [professorParaEditar.preferencia_periodo]
            : [],
      )

      // Converter disponibilidade para novo formato
      const dispOriginal = professorParaEditar.disponibilidade || {}
      const novaDisp = {}
      DIAS_SEMANA.forEach((dia) => {
        if (dispOriginal[dia.key] && Array.isArray(dispOriginal[dia.key]) && dispOriginal[dia.key].length >= 2) {
          novaDisp[dia.key] = {
            entrada: dispOriginal[dia.key][0] || "",
            saida: dispOriginal[dia.key][1] || "",
          }
        } else {
          novaDisp[dia.key] = { entrada: "", saida: "" }
        }
      })
      setDisponibilidade(novaDisp)

      carregarAssociacoes(professorParaEditar.id)
    } else {
      resetForm()
    }
  }, [professorParaEditar, carregarAssociacoes])

  const resetForm = () => {
    setIsEditing(false)
    setNome("")
    setDepartamento("")
    setCurso("")
    setPreferencia([])
    setDisciplinasSelecionadas([])
    setTurmasSelecionadas([])
    setDisponibilidade({
      seg: { entrada: "", saida: "" },
      ter: { entrada: "", saida: "" },
      qua: { entrada: "", saida: "" },
      qui: { entrada: "", saida: "" },
      sex: { entrada: "", saida: "" },
      sab: { entrada: "", saida: "" },
    })
  }

  const handleDisponibilidadeChange = (dia, campo, valor) => {
    setDisponibilidade((prev) => ({
      ...prev,
      [dia]: {
        ...prev[dia],
        [campo]: valor,
      },
    }))
  }

  // Toggle preferência de período
  const togglePreferencia = (periodo) => {
    setPreferencia((prev) => {
      if (prev.includes(periodo)) {
        return prev.filter((p) => p !== periodo)
      } else {
        return [...prev, periodo]
      }
    })
  }

  // Toggle disciplina
  const toggleDisciplina = (discId) => {
    setDisciplinasSelecionadas((prev) => {
      if (prev.includes(discId)) {
        return prev.filter((id) => id !== discId)
      } else {
        return [...prev, discId]
      }
    })
  }

  // Toggle turma
  const toggleTurma = (turmaId) => {
    setTurmasSelecionadas((prev) => {
      if (prev.includes(turmaId)) {
        return prev.filter((id) => id !== turmaId)
      } else {
        return [...prev, turmaId]
      }
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!nome.trim()) {
      alert("Digite o nome do professor!")
      return
    }

    setLoading(true)
    try {
      // Converter disponibilidade para formato de array
      const dispParaSalvar = {}
      DIAS_SEMANA.forEach((dia) => {
        const d = disponibilidade[dia.key]
        if (d.entrada && d.saida) {
          dispParaSalvar[dia.key] = [d.entrada, d.saida]
        } else {
          dispParaSalvar[dia.key] = []
        }
      })

      console.debug("handleSubmit - isAdmin:", isAdmin, "instituicaoId:", instituicaoId)

      if (isEditing && professorParaEditar) {
        console.debug("Editando professor:", professorParaEditar.id)
        let updateQuery = supabase
          .from("professores")
          .update({
            nome,
            departamento_id: departamento || null,
            curso_id: curso || null,
            preferencia_periodo: preferencia,
            disponibilidade: dispParaSalvar,
          })
          .eq("id", professorParaEditar.id)

        if (!isAdmin && instituicaoId) {
          updateQuery = updateQuery.eq("instituicao_id", instituicaoId)
        }

        const { error: updateError } = await updateQuery

        if (updateError) throw updateError

        // Atualizar disciplinas
        console.debug("Deletando professor_disciplina para:", professorParaEditar.id)
        let deleteDiscQuery = supabase
          .from("professor_disciplina")
          .delete()
          .eq("professor_id", professorParaEditar.id)
        if (!isAdmin && instituicaoId) {
          deleteDiscQuery = deleteDiscQuery.eq("instituicao_id", instituicaoId)
        }
        const { error: delDiscError } = await deleteDiscQuery
        if (delDiscError) {
          console.error("Erro ao deletar disciplinas:", delDiscError.message)
          throw delDiscError
        }

        if (disciplinasSelecionadas.length > 0) {
          console.debug("Inserindo disciplinas:", disciplinasSelecionadas)
          const insertsDisc = disciplinasSelecionadas.map((disciplina_id) => ({
            professor_id: professorParaEditar.id,
            disciplina_id,
            instituicao_id: professorParaEditar.instituicao_id,
          }))
          console.log("Payload de disciplinas:", insertsDisc)
          const { error: discError } = await supabase.from("professor_disciplina").insert(insertsDisc)
          if (discError) {
            console.error("Erro ao vincular disciplinas:", discError.message)
            console.error("Payload:", insertsDisc)
            throw discError
          }
        }

        // Atualizar turmas
        console.debug("Deletando professor_turma para:", professorParaEditar.id)
        let deleteTurmaQuery = supabase
          .from("professor_turma")
          .delete()
          .eq("professor_id", professorParaEditar.id)
        if (!isAdmin && instituicaoId) {
          deleteTurmaQuery = deleteTurmaQuery.eq("instituicao_id", instituicaoId)
        }
        const { error: delTurmaError } = await deleteTurmaQuery
        if (delTurmaError) {
          console.error("Erro ao deletar turmas:", delTurmaError.message)
          throw delTurmaError
        }

        if (turmasSelecionadas.length > 0) {
          console.debug("Inserindo turmas:", turmasSelecionadas)
          const insertsTurmas = turmasSelecionadas.map((turma_id) => ({
            professor_id: professorParaEditar.id,
            turma_id,
            instituicao_id: professorParaEditar.instituicao_id,
          }))
          console.log("Payload de turmas:", insertsTurmas)
          const { error: turmaError } = await supabase.from("professor_turma").insert(insertsTurmas)
          if (turmaError) {
            console.error("Erro ao vincular turmas:", turmaError.message)
            console.error("Payload:", insertsTurmas)
            throw turmaError
          }
        }

        alert("Professor atualizado com sucesso!")
        if (onCancelEdit) onCancelEdit()
      } else {
        // Inserir novo professor
        console.debug("Criando novo professor")
        const payload = ensureInstitutionOnPayload({
          nome,
          departamento_id: departamento || null,
          curso_id: curso || null,
          preferencia_periodo: preferencia,
          disponibilidade: dispParaSalvar,
          created_at: new Date().toISOString(),
        }, isAdmin, instituicaoId)
        
        console.log("Payload de professor a criar:", payload)

        const { data: novoProfessor, error: insertError } = await supabase
          .from("professores")
          .insert([payload])
          .select()
          .single()
        
        if (insertError) {
          console.error("Erro ao inserir professor:", insertError.message)
          console.error("Payload:", payload)
          throw insertError
        }

        console.debug("Professor criado com ID:", novoProfessor.id, "instituicao_id:", novoProfessor.instituicao_id)

        // Associar disciplinas
        if (disciplinasSelecionadas.length > 0) {
          console.debug("Inserindo disciplinas para novo professor:", disciplinasSelecionadas)
          const insertsDisc = disciplinasSelecionadas.map((disciplina_id) => ({
            professor_id: novoProfessor.id,
            disciplina_id,
            instituicao_id: novoProfessor.instituicao_id,
          }))
          console.log("Payload de disciplinas:", insertsDisc)
          const { error: discError } = await supabase.from("professor_disciplina").insert(insertsDisc)
          if (discError) {
            console.error("Erro ao vincular disciplinas:", discError.message)
            console.error("Payload:", insertsDisc)
            throw discError
          }
        }

        // Associar turmas
        if (turmasSelecionadas.length > 0) {
          console.debug("Inserindo turmas para novo professor:", turmasSelecionadas)
          const insertsTurmas = turmasSelecionadas.map((turma_id) => ({
            professor_id: novoProfessor.id,
            turma_id,
            instituicao_id: novoProfessor.instituicao_id,
          }))
          console.log("Payload de turmas:", insertsTurmas)
          const { error: turmaError } = await supabase.from("professor_turma").insert(insertsTurmas)
          if (turmaError) {
            console.error("Erro ao vincular turmas:", turmaError.message)
            console.error("Payload:", insertsTurmas)
            throw turmaError
          }
        }

        alert("Professor salvo com sucesso!")
      }

      resetForm()
      if (onAdded) onAdded()
    } catch (err) {
      console.error("Erro ao salvar professor:", err)
      alert("Erro ao salvar professor: " + (err.message || err))
    } finally {
      setLoading(false)
    }
  }

  const handleCancelar = () => {
    resetForm()
    if (onCancelEdit) onCancelEdit()
  }

  return (
    <form onSubmit={handleSubmit} className="professor-form-card">
      <h2 className="form-title">{isEditing ? "Editar Professor" : "Adicionar Professor"}</h2>

      <div className="form-grid">
        <div className="form-group">
          <label>
            Nome do Professor <span className="required">*</span>
          </label>
          <input
            type="text"
            placeholder="Nome completo"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            className="form-input"
            required
          />
        </div>

        <div className="form-group">
          <label>Departamento</label>
          <select value={departamento} onChange={(e) => setDepartamento(e.target.value)} className="form-input">
            <option value="">Selecione...</option>
            {departamentosList.map((d) => (
              <option key={d.id} value={d.id}>
                {d.nome}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Curso</label>
          <select value={curso} onChange={(e) => setCurso(e.target.value)} className="form-input">
            <option value="">Selecione...</option>
            {cursosList.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-group">
        <label>Preferência de Período</label>
        <div className="preferencia-grid">
          {["manha", "tarde", "noite"].map((periodo) => (
            <label key={periodo} className="preferencia-checkbox">
              <input
                type="checkbox"
                checked={preferencia.includes(periodo)}
                onChange={() => togglePreferencia(periodo)}
              />
              <span>{periodo === "manha" ? "Manhã" : periodo === "tarde" ? "Tarde" : "Noite"}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="form-group disponibilidade-section">
        <label>Disponibilidade por Dia</label>
        <p className="helper-text">Defina o horário de entrada e saída para cada dia</p>
        <div className="disponibilidade-grid">
          {DIAS_SEMANA.map((dia) => (
            <div key={dia.key} className="dia-disponibilidade">
              <span className="dia-label">{dia.label}</span>
              <div className="horarios-inputs">
                <div className="horario-group">
                  <label>Entrada</label>
                  <input
                    type="time"
                    value={disponibilidade[dia.key].entrada}
                    onChange={(e) => handleDisponibilidadeChange(dia.key, "entrada", e.target.value)}
                    className="form-input time-input"
                  />
                </div>
                <div className="horario-group">
                  <label>Saída</label>
                  <input
                    type="time"
                    value={disponibilidade[dia.key].saida}
                    onChange={(e) => handleDisponibilidadeChange(dia.key, "saida", e.target.value)}
                    className="form-input time-input"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Disciplinas como checkboxes */}
      <div className="form-group">
        <label>Disciplinas que Leciona</label>
        <div className="selecao-grid">
          {disciplinasList.map((d) => (
            <label key={d.id} className="selecao-checkbox">
              <input
                type="checkbox"
                checked={disciplinasSelecionadas.includes(d.id)}
                onChange={() => toggleDisciplina(d.id)}
              />
              <span>{d.nome}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Turmas como checkboxes */}
      <div className="form-group">
        <label>Turmas Atribuídas</label>
        <div className="selecao-grid">
          {turmasList.map((t) => (
            <label key={t.id} className="selecao-checkbox">
              <input type="checkbox" checked={turmasSelecionadas.includes(t.id)} onChange={() => toggleTurma(t.id)} />
              <span>{t.nome}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="form-actions">
        {isEditing && (
          <button type="button" className="btn-cancelar" onClick={handleCancelar}>
            Cancelar
          </button>
        )}
        <button type="submit" className="btn-salvar" disabled={loading}>
          {loading ? "Salvando..." : isEditing ? "Atualizar" : "Salvar"}
        </button>
      </div>
    </form>
  )
}
